import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { cardsFrom, filterCards, groupTypeName, sortCards } from "./oneListCards";
import { Message } from "components/pages/WarmUpPicker/styles";
import { OneListQuickCard, quickKebabId } from "./OneListQuickCard";
import { QuickGroupQuestionsModal, quickGroupPreviewTarget } from "./QuickGroupQuestionsModal";
import { WarmUpGroupCard } from "./WarmUpGroupCard";
import { useWarmUpCards } from "./useWarmUpCards";
import { useStableOrder } from "./useStableOrder";
import { useFocusAfterRemoval } from "./useFocusAfterRemoval";
import { lessonEditButtonId } from "./OneListLessonCard";
import { kebabId } from "./WarmUpGroupCard";

// Every card of the class in one column: lesson bundles, quick groups and
// warm-up assignments together.
const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const cardKey = (card) => `${card.type}:${card.data.assignment_id || card.data.id}`;

// The control that takes focus for a card: a lesson's Edit, else its kebab.
const PRIMARY_CONTROL_ID = {
  lesson: lessonEditButtonId,
  quick: quickKebabId,
  warmUp: kebabId,
};

const primaryControlOf = (key) => {
  const [type, id] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];
  return document.getElementById(PRIMARY_CONTROL_ID[type]?.(id));
};

export const OneList = ({ lessons, quickGroups, warmUps, classroomId, renderLesson, warmUpActions, sortBy, groupType, focusedKey = null, sourcesReadAt, onNoCardLeft, onQuickGroupsChanged, eventProps, guardNavigation }) => {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const allCards = cardsFrom({ lessons, quickGroups, warmUps });
  const sorted = filterCards(sortCards(allCards, sortBy), groupType);
  // An archive that empties the class is told at once: the list is replaced by
  // the empty state before it could move focus itself.
  const cardArchived = (key) => {
    if (allCards.length === 1 && cardKey(allCards[0]) === key) onNoCardLeft?.();
  };
  const { shown, holdOrder } = useStableOrder(sorted, cardKey, [lessons, quickGroups, warmUps, sourcesReadAt], [sortBy, groupType]);
  // The teacher's own expand/collapse choices, per card, for this class.
  const [expandChoices, setExpandChoices] = useState({});
  useEffect(() => {
    if (focusedKey) setExpandChoices((choices) => ({ ...choices, [focusedKey]: true }));
  }, [focusedKey]);
  // An untouched lesson card is open only while it is the first lesson shown.
  const topLesson = shown.find((card) => card.type === "lesson");
  const topLessonKey = topLesson ? cardKey(topLesson) : null;
  const isExpanded = (card) => expandChoices[cardKey(card)] ?? cardKey(card) === topLessonKey;
  const toggle = (card) => setExpandChoices((choices) => ({ ...choices, [cardKey(card)]: !isExpanded(card) }));

  const warmUpCards = useWarmUpCards({
    ...warmUpActions,
    classroomId,
    openMenuId,
    setOpenMenuId,
    onSavedInPlace: holdOrder,
    onCardArchived: (assignment) => cardArchived(`warmUp:${assignment.id}`),
  });
  // Focused edit keeps the edited card mounted in place and hides the others.
  const cards = focusedKey ? shown.filter((card) => cardKey(card) === focusedKey) : shown;
  useFocusAfterRemoval(cards.map(cardKey), primaryControlOf, onNoCardLeft);
  const renderCard = (card) => {
    if (card.type === "lesson") {
      return renderLesson(card.data, {
        expanded: isExpanded(card),
        onToggleExpanded: () => toggle(card),
        editButtonId: lessonEditButtonId(card.data.assignment_id),
        onArchived: () => cardArchived(cardKey(card)),
      });
    }
    if (card.type === "quick") {
      const menuId = cardKey(card);
      return (
        <OneListQuickCard
          quickGroup={card.data}
          classroomId={classroomId}
          menuOpen={openMenuId === menuId}
          onMenuOpenChange={(open) => setOpenMenuId((current) => (open ? menuId : current === menuId ? null : current))}
          onChanged={onQuickGroupsChanged}
          onPreview={() => setPreviewTarget(quickGroupPreviewTarget(card.data))}
          onArchived={() => cardArchived(menuId)}
          eventProps={eventProps}
          guardNavigation={guardNavigation}
        />
      );
    }
    return <WarmUpGroupCard {...warmUpCards.cardProps(card.data)} />;
  };
  return (
    <>
      {cards.length === 0 ? (
        <Message role="status">{`No ${groupTypeName(groupType)} in this class yet.`}</Message>
      ) : (
        <List aria-label="Groups">
          {cards.map((card) => (
            <li key={cardKey(card)}>{renderCard(card)}</li>
          ))}
        </List>
      )}
      {warmUpCards.dialogs}
      <QuickGroupQuestionsModal
        target={previewTarget}
        classroomId={classroomId}
        onClose={() => setPreviewTarget(null)}
        onChanged={onQuickGroupsChanged}
      />
    </>
  );
};
